import React, { useState, useEffect } from 'react';

const Step8Funding = ({
  setCurrentStep,
  handleSubmitStep7,
  isSubmitting,
  fundingForm,
  handleFundingInputChange,
  submitError
}) => {
  // Estados para el selector de países
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countryError, setCountryError] = useState(null);

  // Estados para el selector de estados
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [stateError, setStateError] = useState(null);

  // Efecto para cargar la lista de países al iniciar el componente
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      setCountryError(null);
      try {
        const jwt = localStorage.getItem('jwt');
        const response = await fetch('https://pt-api.digit-a.net/api/catalogue/countries', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch countries: ${response.status}`);
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          // Convertir el array de strings a array de objetos
          const formattedCountries = data.map(countryName => ({
            code: countryName,
            name: countryName
          }));

          // Ordenar los países para colocar "United States" primero
          const sortedCountries = formattedCountries.sort((a, b) => {
            if (a.name === "United States") return -1;
            if (b.name === "United States") return 1;
            return a.name.localeCompare(b.name); // Ordenar el resto alfabéticamente
          });

          setCountries(sortedCountries);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (error) {
        console.error('Error loading countries:', error);
        setCountryError(`Error loading countries: ${error.message}`);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  // Efecto para cargar estados cuando el país es Estados Unidos
  useEffect(() => {
    // Verificar si el país seleccionado es Estados Unidos
    if (fundingForm.country !== 'United States') {
      setStates([]);
      return;
    }

    const fetchStates = async () => {
      setLoadingStates(true);
      setStateError(null);
      try {
        const jwt = localStorage.getItem('jwt');
        // Enviamos el nombre completo del país
        const requestBody = {
          country: 'United States'
        };

        const response = await fetch('https://pt-api.digit-a.net/api/catalogue/states-by-country', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch states: ${response.status}`);
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          const formattedStates = data.map(stateName => ({
            code: stateName,
            name: stateName
          }));
          setStates(formattedStates);
        } else {
          throw new Error('Invalid state data format');
        }
      } catch (error) {
        console.error('Error fetching states:', error);
        setStateError('Error loading states');
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, [fundingForm.country]);

  // Determinar si el campo State debe mostrarse
  const showStateField = fundingForm.country === 'United States';

  return (
    <div className="modal_content_body">
      <h3 className="ppt_form_title mb-5 text-center">Step 7: Funding Details</h3>
      <div className="row">
        <div className="col-12 col-lg-6">
          <div className="ppt_input_style mb-4">
            <label>Bank Name <abbr>*</abbr></label>
            <div>
              <input
                type="text"
                className="form-control"
                name="bankName"
                value={fundingForm.bankName}
                onChange={handleFundingInputChange}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="ppt_input_style mb-4">
            <label>Account Number <abbr>*</abbr></label>
            <div>
              <input
                type="tel"
                className="form-control"
                name="accountNumber"
                value={fundingForm.accountNumber}
                onChange={handleFundingInputChange}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="ppt_input_style mb-4">
            <label>Funding Type <abbr>*</abbr></label>
            <div>
              <select
                className="form-select"
                name="fundingType"
                value={fundingForm.fundingType}
                onChange={handleFundingInputChange}
              >
                <option value=""></option>
                <option value="SWIFT">SWIFT</option>
                <option value="SWIFT 1">SWIFT 1</option>
                <option value="SWIFT 2">SWIFT 2</option>
              </select>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="ppt_input_style mb-4">
            <label>SWIFT Code/BIC <abbr>*</abbr></label>
            <div>
              <input
                type="tel"
                className="form-control"
                name="swiftCodeBic"
                value={fundingForm.swiftCodeBic}
                onChange={handleFundingInputChange}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="ppt_input_style mb-4">
            <label>Country <abbr>*</abbr></label>
            <div>
              <select
                className="form-select"
                name="country"
                value={fundingForm.country}
                onChange={handleFundingInputChange}
                disabled={loadingCountries}
              >
                <option value="">Select a country</option>
                {loadingCountries ? (
                  <option value="" disabled>Loading countries...</option>
                ) : (
                  countries.map((country, index) => (
                    <option
                      key={index}
                      value={country.code}
                    >
                      {country.name}
                    </option>
                  ))
                )}
              </select>
              {countryError && (
                <small className="text-danger">{countryError}</small>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="ppt_input_style mb-4">
            <label>Bank Address <abbr>*</abbr></label>
            <div>
              <input
                type="text"
                className="form-control"
                name="bankAddress"
                value={fundingForm.bankAddress}
                onChange={handleFundingInputChange}
              />
            </div>
          </div>
        </div>

        {/* El campo State solo se muestra cuando el país es Estados Unidos */}
        {showStateField && (
          <div className="col-12 col-lg-6">
            <div className="ppt_input_style mb-4">
              <label>State <abbr>*</abbr></label>
              <div>
                <select
                  className="form-select"
                  name="state"
                  value={fundingForm.state}
                  onChange={handleFundingInputChange}
                  disabled={loadingStates}
                >
                  <option value="">Select a state</option>
                  {loadingStates ? (
                    <option value="" disabled>Loading states...</option>
                  ) : (
                    states.map((state, index) => (
                      <option
                        key={index}
                        value={state.code}
                      >
                        {state.name}
                      </option>
                    ))
                  )}
                </select>
                {stateError && (
                  <small className="text-danger">{stateError}</small>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="col-12 col-lg-6">
          <div className="ppt_input_style mb-4">
            <label>ZIP Code <abbr>*</abbr></label>
            <div>
              <input
                type="text"
                className="form-control"
                name="zipCode"
                value={fundingForm.zipCode}
                onChange={handleFundingInputChange}
              />
            </div>
          </div>
        </div>
      </div>
      {submitError && (
        <div className="alert alert-danger mb-4">
          {submitError}
        </div>
      )}
      <div className="d-flex justify-content-end gap-3 pt-4 kyc_btns">
        {/* <button onClick={() => setCurrentStep(7)} className="ppt_btn_secondary alt text-dark">Back</button> */}
        <button
          onClick={handleSubmitStep7}
          className="ppt_btn_primary alt"
          disabled={
            isSubmitting ||
            !fundingForm.bankName ||
            !fundingForm.fundingType ||
            !fundingForm.country ||
            !fundingForm.accountNumber ||
            !fundingForm.swiftCodeBic ||
            !fundingForm.bankAddress ||
            !fundingForm.zipCode ||
            (showStateField && !fundingForm.state) // Validar state solo cuando es Estados Unidos
          }
        >
          {isSubmitting ? 'Submitting...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default Step8Funding;